// @flow
import { useEffect, useMemo, useRef, useCallback, useState } from 'react';
// Helpers
import { logAPIException } from '../../logHelper';
// Utils
import { isMinAmountPromotionCompleted } from '../utils/isMinAmountPromotionCompleted';
// Types
import type { LineItem, Promotion } from '../types';

const FREEBIE_PROMOTION_TYPE = 'Spree::Promotion::Actions::CreateFreebie';
const REFRESH_INTERVAL = 2000;
const MAX_RETRIES = 5;

type UseOrderPromotionRefetchParams = {
  promotions: Promotion[],
  items: LineItem[],
  refetchOrder: () => Promise<void>,
  totalPrice: number,
};

export const useOrderPromotionRefetch = ({
  promotions,
  items,
  refetchOrder,
  totalPrice,
}: UseOrderPromotionRefetchParams) => {
  const [isBackgroundRetry, setIsBackgroundRetry] = useState(false);

  const isFetchingRef = useRef(false);
  const retryCountRef = useRef(0);
  const previousEligibilityRef = useRef(null);

  const freebiePromotions = useMemo(() => {
    return (
      promotions?.filter(promo =>
        promo.promotionActions?.some(
          promoAction => promoAction.type === FREEBIE_PROMOTION_TYPE,
        ),
      ) || []
    );
  }, [promotions]);

  const hasFreebiePromotion = freebiePromotions.length > 0;

  const isFreebiePromotionCompleted = useMemo(() => {
    return freebiePromotions.some(promo =>
      isMinAmountPromotionCompleted(promo, totalPrice),
    );
  }, [freebiePromotions, totalPrice]);

  const hasCompletedFreebiePromotion =
    hasFreebiePromotion && isFreebiePromotionCompleted;

  const hasFreebieLineItem = useMemo(() => {
    if (items?.length > 0) {
      return items.some(item => !!item?.isFreebie);
    }
    return false;
  }, [items]);

  useEffect(() => {
    if (
      previousEligibilityRef.current !== null &&
      previousEligibilityRef.current !== hasCompletedFreebiePromotion
    ) {
      retryCountRef.current = 0;
      setIsBackgroundRetry(false);
    }
    previousEligibilityRef.current = hasCompletedFreebiePromotion;
  }, [hasCompletedFreebiePromotion]);

  const fetchOrder = useCallback(async () => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;

    if (retryCountRef.current > 0) {
      setIsBackgroundRetry(true);
    }

    try {
      await refetchOrder();
    } catch (error) {
      logAPIException(error, { context: 'useOrderPromotionRefetch' });
    } finally {
      retryCountRef.current += 1;
      isFetchingRef.current = false;
    }
  }, [refetchOrder]);

  useEffect(() => {
    const isMissingFreebie =
      hasCompletedFreebiePromotion && !hasFreebieLineItem;
    const hasUndesiredFreebie =
      !hasCompletedFreebiePromotion && hasFreebieLineItem;

    const shouldRefetch =
      (isMissingFreebie || hasUndesiredFreebie) &&
      retryCountRef.current < MAX_RETRIES;

    if (shouldRefetch) {
      const intervalId = setInterval(() => {
        fetchOrder();
      }, REFRESH_INTERVAL);

      return () => {
        clearInterval(intervalId);
      };
    } else if (!shouldRefetch && isBackgroundRetry) {
      setIsBackgroundRetry(false);
    }
  }, [
    hasCompletedFreebiePromotion,
    hasFreebieLineItem,
    fetchOrder,
    isBackgroundRetry,
  ]);

  return { isBackgroundRetry };
};
